/* eslint-disable */ // InitiativesManagement.js
// ######====== IMPORTS GENERAL
import React, { useContext, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { makeStyles } from "@material-ui/core/styles";
import { ThreeDots } from "react-loader-spinner";
import ReactSelect from "react-select";
import PropTypes from "prop-types";

// Material-UI Core
import {
  Typography,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Button,
  Drawer,
  useMediaQuery,
  useTheme,
  Switch,
} from "@material-ui/core";

// Material-UI Icons
import Assignment from "@material-ui/icons/Assignment";
//import TextEditor from "components/TextEditor/TextEditor.js";
import AttachMoney from "@material-ui/icons/AttachMoney";
import AddCircle from "@material-ui/icons/AddCircle";
import Edit from "@material-ui/icons/Edit";
import Event from "@material-ui/icons/Event";
import Search from "@material-ui/icons/Search";
import ViewQuilt from "@material-ui/icons/ViewQuilt";
import IconButton from "@material-ui/core/IconButton";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FilterListIcon from "@material-ui/icons/FilterList";
import ChecklistRtlIcon from "@material-ui/icons/AssignmentTurnedIn";
import { formatNumber } from "utils/formatNumberUtils.js";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";

// Custom Components
import Card from "../../components/Card/Card.js";
import CardBody from "../../components/Card/CardBody.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardIcon from "../../components/Card/CardIcon.js";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import NewInitiativeForm from "./newInitiativeForm.js";
import OnePage from "./OnePage_v2.js";
import InitiativesImplementation from "./initiativesImplementation";
import InitiativeResultsPage from "./initiativesResults";
import InitiativeDetails from "./initiativeDetails.js";
import { columnToCanonical } from "utils/labelRegistry";
import { canonicalToTKey } from "utils/labelRegistry";
import { getAltLabel } from "utils/getAltLabel";
import { listDeliverables } from "services/deliverablesApi";
import ConsolidatedInitiativesGantt from "./InitiativesConsolidatedGantt.js";
import ApprovalsSection from "components/Approval/ApprovalsSection.js";
import ApprovalsFloating from "components/Approval/ApprovalsFloating.js";
import { approvalsApi } from "../../services/approvalsApi";
import StatusWarningIndicator from "../../components/StatusWarningIndicator";

// Data Mocks
//import { onePageInfoMock, onePageHistoryMock } from "./DataMocks";

// Utility Libraries
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilter,
  faSort,
  faSortDown,
  faSortUp,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";

// Context and Styles
import AuthContext from "../../contexts/AuthProvider.js";
import styles from "../../assets/jss/material-dashboard-pro-react/views/initiativesManagementStyle.js";
import {
  Feedback,
  Visibility,
  Cancel,
  HourglassEmpty,
} from "@material-ui/icons";
import { Tabs, Tab } from "@material-ui/core";
import { client } from "../../services/axiosClient";

// ######====== PORTAL FOR DRAGGABLE (Z-INDEX FIX)
// Portal customizado para renderizar o elemento arrastado no nível mais alto do DOM
const portal = document.createElement('div');
portal.style.position = 'absolute';
portal.style.top = '0';
portal.style.left = '0';
portal.style.zIndex = '9999';
if (!document.body.contains(portal)) {
  document.body.appendChild(portal);
}

// ######====== MAIN COMPONENT
export default function InitiativesManagement() {
  const { userDetails } = useContext(AuthContext);

  const client_styles = (theme) => ({
    ...styles(theme, userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();
  const { t, i18n } = useTranslation();


    const defaultSummaryCardsLayout = [
    { key: "total_initiatives", label_key: "initiatives.total_initiatives", type: "count" },
    { key: "sum_kpi_actual", label_key: "initiatives.columns.kpi_actual", type: "number_sum" },
    { key: "sum_kpi_planned_total", label_key: "initiatives.columns.kpi_planned", type: "number_sum", show_in_board: 1.0 },
    { key: "percent_captured", label_key: "initiatives.columns.kpi_ratio", type: "percentage_avg" },
    // Campos de deliverables para Porto Seguro
    { key: "sum_deliverables_completed", label_key: "initiatives.columns.deliverables_completed", type: "number_sum" },
    { key: "sum_deliverables_planned", label_key: "initiatives.deliverables.expected_today", type: "number_sum" },
    { key: "sum_readiness_percent", label_key: "initiatives.columns.readiness_percent", type: "percentage_avg" },
    // NOVOS CAMPOS: MVP e Launch Readiness
    { key: "avg_mvp_readiness", label_key: "initiatives.readiness.mvp", type: "percentage_avg" },
    { key: "avg_launch_readiness", label_key: "initiatives.readiness.launch", type: "percentage_avg" },
  ];

  const defaultTableLayout = {
    columns: [
      { key: "status", header_key: "initiatives.columns.status", sortable: true, type: "status_color" },
      { key: "branch", header_key: "initiatives.columns.branch", sortable: true, type: "text" },
      { key: "workfront", header_key: "initiatives.columns.workfront", sortable: true, type: "text" },
      { key: "initiative", header_key: "initiatives.columns.initiative", sortable: true, type: "link_text" },
      { key: "stage", header_key: "initiatives.columns.stage", sortable: true, type: "stage" },
      { key: "leader", header_key: "initiatives.columns.leader", sortable: true, type: "text" },
      { key: "kpi_actual", header_key: "initiatives.columns.kpi_actual", sortable: false, type: "number" },
      { key: "kpi_planned", header_key: "initiatives.columns.kpi_planned", sortable: false, type: "number" },
      { key: "kpi_ratio", header_key: "initiatives.columns.kpi_ratio", sortable: false, type: "percentage" },
      // Campos de deliverables para Porto Seguro
      { key: "deliverables_completed", header_key: "initiatives.columns.deliverables_completed", sortable: false, type: "number" },
      { key: "deliverables_planned", header_key: "initiatives.columns.deliverables_planned", sortable: false, type: "number" },
      { key: "readiness_percent", header_key: "initiatives.columns.readiness_percent", sortable: false, type: "percentage" },
    ]
  };

  const summaryCardsLayout = (userDetails.initiative_summary_cards && userDetails.initiative_summary_cards.length > 0)
    ? userDetails.initiative_summary_cards
    : defaultSummaryCardsLayout;

  const tableLayout = (userDetails.initiative_list_layout && userDetails.initiative_list_layout.columns?.length > 0)
    ? userDetails.initiative_list_layout
    : defaultTableLayout;


  useEffect(() => {
    moment.locale(i18n.language);
  }, [i18n.language]);
  const clientColor = userDetails?.implementation_color;

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "80px",
      maxHeight: "80px", // Altura máxima do campo
      overflowY: "auto",
      boxSizing: "border-box",
      borderColor: state.isFocused ? clientColor : provided.borderColor, // muda cor da borda ao focar
      backgroundColor: "white", // cor de fundo do campo
      "&:hover": {
        borderColor: clientColor, // cor da borda ao passar o mouse
      },
    }),
    menu: (provided) => ({
      ...provided,
      //maxHeight: "200px", // Altura máx do dropdown
      overflowY: "auto", // scroll interno quando muitas opções
      backgroundColor: "white",
      boxSizing: "border-box",
    }),
    option: (provided, state) => ({
      ...provided,
      //color: state.isSelected ? "white" : "black",
      color: state.isSelected
        ? "black"
        : state.isFocused
          ? "white" // cor do mouse over nos itens do dropdown
          : "black",
      backgroundColor: state.isSelected
        ? "gray"
        : state.isFocused
          ? clientColor // cor do mouse over nos itens do dropdown
          : "white", // cor da lista não selecioana no dropdown
      "&:active": {
        transform: "scale(0.95)", // Dá uma "afundada" no botão ao clicar
        transition: "transform 0.2s ease",
        // Você pode alterar também a cor, caso queira diferenciar ao clicar:
        backgroundColor: clientColor,
      },
    }),

    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#d3d3d3", // cor de fundo das tags selecionadas
    }),

    multiValueLabel: (provided) => ({
      ...provided,
      color: "black", // cor do texto dentro das tags
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "black",
      ":hover": {
        backgroundColor: "red",
        color: "white",
      },
    }),
  };

  const [initiatives, setInitiatives] = useState([]);
  const [stages, setStages] = useState([]);
  const [draggingInitiative, setDraggingInitiative] = useState(null); // Para validação de drop

  const fetchInitiatives = async (reselectId = null) => {
    try {
      setIsLoading(true);
      
      // POR-13 usa API customizada para calcular saving dos custom fields
      const useCustomAPI = userDetails?.implementation_id === 13;
      const apiEndpoint = useCustomAPI 
        ? "/api/por13-initiatives-list-board/" 
        : "/api/initiatives_list_board/";
      
      const response = await client.post(apiEndpoint, {
        action: "list",
      });
      if (response.data && response.data.success) {
        // Lista recarregada com sucesso
        setInitiatives(response.data.initiatives);
        // console.log("🚀 Initiatives recebidas:", response.data.initiatives);
        // console.log("🔍 Exemplo de iniciativa com deliverables:", response.data.initiatives[0]);
        
        // Atualiza o gantt_mode no userDetails se disponível
        if (response.data.gantt_mode && userDetails) {
          userDetails.gantt_mode = response.data.gantt_mode;
        }

        // Se foi passado reselectId (da criação ou edição), force a seleção
        if (reselectId) {
          console.log("🔄 DEBUG fetchInitiatives - Tentando reselecionar iniciativa:", reselectId);
          // Verificar se a iniciativa existe na lista recarregada
          const foundInitiative = response.data.initiatives.find(init => init.id === reselectId);
          console.log("🔄 DEBUG fetchInitiatives - Iniciativa encontrada na lista:", foundInitiative ? foundInitiative.initiative : "NÃO ENCONTRADA");
          
          // Otimização: usar dados já carregados para evitar segunda chamada à API
          if (foundInitiative) {
            
            // Atualizar o estado diretamente com os dados da lista (preview rápido)
            // IMPORTANTE: Garantir que TODOS os arrays tenham valores padrão seguros
            setSelectedInitiative({
              id: foundInitiative.id,
              initiative: foundInitiative.initiative,
              leader: foundInitiative.leader || "",
              leader2: foundInitiative.leader2 || "", 
              pmo: foundInitiative.pmo || "",
              workfront: foundInitiative.workfront || "",
              branch: foundInitiative.branch || "",
              stage: foundInitiative.stage || "",
              description: foundInitiative.description || "",
              // Valores padrão seguros para evitar erros de undefined.map()
              initiativeImpacts: Array.isArray(foundInitiative.initiativeImpacts) ? foundInitiative.initiativeImpacts : [],
              tags: Array.isArray(foundInitiative.tags) ? foundInitiative.tags : [],
              accessGroups: Array.isArray(foundInitiative.accessGroups) ? foundInitiative.accessGroups : [],
              internalCode: foundInitiative.internalCode || "",
              customFieldsMetadata: Array.isArray(foundInitiative.customFieldsMetadata) ? foundInitiative.customFieldsMetadata : [],
              customFields: (foundInitiative.customFields && typeof foundInitiative.customFields === 'object') ? foundInitiative.customFields : {},
              editors: (foundInitiative.editors && typeof foundInitiative.editors === 'object') ? {
                one_page: Array.isArray(foundInitiative.editors.one_page) ? foundInitiative.editors.one_page : [],
                implementation: Array.isArray(foundInitiative.editors.implementation) ? foundInitiative.editors.implementation : [],
                results: Array.isArray(foundInitiative.editors.results) ? foundInitiative.editors.results : []
              } : {
                one_page: [],
                implementation: [],
                results: []
              },
              // Outros campos que podem ser necessários
              plannedStartDate: foundInitiative.plannedStartDate || "",
              plannedEndDate: foundInitiative.plannedEndDate || "",
              realStartDate: foundInitiative.realStartDate || "",
              realEndDate: foundInitiative.realEndDate || "",
              // Campos de datas do results
              results_start_date: foundInitiative.results_start_date || "",
              results_end_date: foundInitiative.results_end_date || ""
            });
            
            setSelectedSection("details");
            setIsTableCollapsed(true);
            setIsLoadingDetails(false);
            
            // Fazer a chamada completa em background para dados detalhados (sem loading visual)
            setTimeout(() => {
              handleInitiativeClick(reselectId, "details", true); // Passar flag para indicar background
            }, 100);
          } else {
            // Fallback: fazer chamada completa se não encontrar na lista
            setTimeout(() => {
              handleInitiativeClick(reselectId);
              setSelectedSection("details");
              setIsTableCollapsed(true);
            }, 200);
          }
        }
      } else {
        // console.error("Failed to fetch initiatives:", response.data);
      }
    } catch (error) {
      // console.error("Error fetching initiatives:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStages = async () => {
    try {
      const res = await client.post("/api/initiative-stage-choices/", {
        action: "list",
      });
      if (res.data.success) {
        setStages(res.data.stages); // por ex. ["draft","implementation","results","concluded","inactive"]
      }
    } catch (err) {
      // console.error("Erro ao buscar stages:", err);
    }
  };

  // Wrapper para garantir que o ID seja passado corretamente para reloadInitiatives
  const reloadInitiativesWrapper = (reselectId) => {
    return fetchInitiatives(reselectId);
  };

  // 2) call it in the effect
  useEffect(() => {
    fetchInitiatives();
    fetchStages();
  }, []);

  const [approvalsLoaded, setApprovalsLoaded] = useState(false);
  const [approvalsData, setApprovalsData] = useState({
    actions: [],
    history: [],
    flows: [],
  });

  const [loadingInitiativeId, setLoadingInitiativeId] = useState(null);

  const reloadApprovalsData = async () => {
    const [a, f, h] = await Promise.all([
      approvalsApi.inbox(),
      approvalsApi.myInitiatives(),
      approvalsApi.myHistory(),
    ]);
    setApprovalsData({
      actions: a.actions || [],
      flows: f.flows || [],
      history: h.history || [],
    });
    setApprovalsLoaded(true);
  };

  //######====== COMPONENT STATES AND VARIABLES
  // Configure o idioma com base no locale do usuário
  const userLocale = navigator.language || "en"; // Detecta o idioma do navegador
  moment.locale(userLocale); // Define o idioma globalmente no Moment.js
  const [selectedInitiative, setSelectedInitiative] = useState(null);
  const [selectedInitiativeCustomFields, setSelectedInitiativeCustomFields] =
    useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [initiativesView, setInitiativesView] = useState("table");

  const [initiativeKpisData, setInitiativeKpisData] = useState(null);
  const [, setIsKpiLoading] = useState(false);
  const [, setKpiError] = useState(null);

  //const [selectedOnePageDate, setSelectedOnePageDate] = useState("");
  const [onePageInfoData, setOnePageInfoData] = useState(null);
  const [onePageError, setOnePageError] = useState(null);

  //######====== HOOKS AND API CALLS
  // API to get KPI data
  const fetchKpiData = async () => {
    try {
      setIsKpiLoading(true);
      setKpiError(null);

      const response = await client.post("/api/initiative-results-data/", {
        action: "list",
        initiative_id: selectedInitiative.id,
      });

      if (response.data.success) {
        setInitiativeKpisData(response.data.data);
      } else {
        setKpiError(response.data.error || "Erro ao buscar KPIs");
      }
    } catch (err) {
      // console.error("Erro ao buscar dados de KPIs:", err);
      setKpiError("Não foi possível contatar o servidor");
    } finally {
      setIsKpiLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedInitiative) {
      setInitiativeKpisData(null);
      return;
    }
    fetchKpiData();
  }, [selectedInitiative]);

  //const [isResultsExpanded, setIsResultsExpanded] = useState(false);

  // HOOK and API to fetch OnePage Data (QUINAUD) - OPTIMIZED VERSION
  const fetchOnePageData = async (initiativeId = null) => {
    const targetId = initiativeId || selectedInitiative?.id;

    
    if (!targetId) {

      return;
    }

    const startTime = performance.now();
    
    try {
      setOnePageError(null);

      
      const response = await client.post("/api/initiative-onepage-data/", {
        action: "list",
        initiative_id: targetId,
      });
      
      const endTime = performance.now();

      
      if (response.data.success) {

        setOnePageInfoData(response.data.data);
      } else {
        console.error('❌ [OPTIMIZATION] OnePage API returned error:', response.data.error);
        setOnePageError(response.data.error || "Fail to load OnePage Data");
      }
    } catch (err) {
      const endTime = performance.now();
      console.error(`❌ [OPTIMIZATION] OnePage data failed after ${(endTime - startTime).toFixed(2)}ms:`, err);
      setOnePageError(err);
    }
  };

  useEffect(() => {

    
    if (!selectedInitiative) {

      setOnePageInfoData(null);
      return;
    }

    // OPTIMIZATION: Only fetch OnePage data if we have a complete initiative object (not just the temp one)
    if (selectedInitiative.initiative === "Loading...") {

      return;
    }
    

    fetchOnePageData(); // chama a função
  }, [selectedInitiative]);

  // Debug useEffect para selectedInitiative
  useEffect(() => {
    // console.log("selectedInitiative changed:", selectedInitiative);
    if (selectedInitiative) {
      // console.log("selectedInitiative.id:", selectedInitiative.id);
      // console.log("selectedInitiative.initiative:", selectedInitiative.initiative);
    }
  }, [selectedInitiative]);

  const [dataDeliverables, setDataDeliverables] = useState(null); // começa sem dados
  const [loadingDeliverables, setLoadingDeliverables] = useState(true);

  useEffect(() => {
    if (!selectedInitiative) {
      setDataDeliverables(null);
      return;
    }

    (async () => {
      setLoadingDeliverables(true);
      try {
        const { data } = await client.post("/api/initiative_deliverables/", {
          action: "list",
          initiative_id: selectedInitiative.id, // agora é sempre um id válido
        });
        setDataDeliverables(data); // ① usa resp.data
      } catch (err) {
        // console.error("erro deliverables:", err);
        setDataDeliverables(null); // evita undefined
      } finally {
        setLoadingDeliverables(false);
      }
    })();
  }, [selectedInitiative]);

  // const [deliverablesData, setDeliverablesData] = useState({
  //   deliverables: [],
  //   deliverables_summary: {
  //     percent_real:    0,
  //     percent_planned: 0,
  //     percent_diff:    0,
  //   },
  // });
  // const [loadingDeliv, setLoadingDeliv] = useState(false);

  // useEffect(() => {
  //   if (!selectedInitiative) {
  //     // nada selecionado → zera
  //     setDeliverablesData({
  //       deliverables: [],
  //       deliverables_summary: { percent_real:0, percent_planned:0, percent_diff:0 },
  //     });
  //     return;
  //   }

  //   setLoadingDeliv(true);
  //   listDeliverables(selectedInitiative.id)
  //     .then(({ data }) => {
  //       if (data.success) {
  //         setDeliverablesData({
  //           deliverables:        data.deliverables        || [],
  //           deliverables_summary:data.deliverables_summary|| {
  //             percent_real:0, percent_planned:0, percent_diff:0,
  //           },
  //         });
  //       } else {
  //         // backend respondeu success:false ─ trata como vazio
  //         setDeliverablesData({
  //           deliverables: [],
  //           deliverables_summary:{ percent_real:0, percent_planned:0, percent_diff:0 },
  //         });
  //       }
  //     })
  //     .catch(err => {
  //       console.error("Erro ao buscar deliverables:", err);
  //       setDeliverablesData({
  //         deliverables: [],
  //         deliverables_summary:{ percent_real:0, percent_planned:0, percent_diff:0 },
  //       });
  //     })
  //     .finally(() => setLoadingDeliv(false));
  // }, [selectedInitiative]);

  useEffect(() => {
    // dispara tudo em paralelo na 1ª renderização
    Promise.all([
      approvalsApi.inbox(), // {actions: [...]}
      approvalsApi.myInitiatives(), // {flows:   [...]}
      approvalsApi.myHistory(), // {history: [...]}
    ]).then(([a, f, h]) => {
      setApprovalsData({
        actions: a.actions || [],
        flows: f.flows || [],
        history: h.history || [],
      });
      setApprovalsLoaded(true);
    });
  }, []);

  const addTransparency = (hexColor, alpha) => {
    if (!hexColor || !/^#([A-Fa-f0-9]{6})$/.test(hexColor)) {
      // console.error("Cor inválida fornecida:", hexColor);
      return `rgba(0, 0, 0, ${alpha})`; // Retorna preto como fallback
    }

    const rgb = hexColor
      .replace(/^#/, "")
      .match(/.{2}/g)
      .map((value) => parseInt(value, 16));

    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
  };

  const [filters, setFilters] = useState({
    status: "",
    initiative: "",
    branch: "",
    workfront: "",
    leader: "",
    tag: "",
  });
  const [selectedSection, setSelectedSection] = useState("details");
  
  // DEBUG: Monitor section changes
  useEffect(() => {

    // Se mudou para one_page e já temos uma iniciativa selecionada, mas não temos dados de OnePage
    if (selectedSection === "one_page" && selectedInitiative && selectedInitiative.initiative !== "Loading..." && !onePageInfoData) {

      fetchOnePageData();
    }
  }, [selectedSection, selectedInitiative, onePageInfoData]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [initiativeToEdit, setInitiativeToEdit] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const toggleDrawer = (open) => {
    setIsDrawerOpen(open);
  };
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  // eslint-disable-next-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const statusOrder = {
    Red: 1,
    Orange: 2,
    Green: 3,
  };

  const sortedAndFilteredInitiatives = initiatives
    .filter((initiative) => {
      return (
        (filters.status === "" || initiative.status === filters.status) &&
        (filters.initiative === "" ||
          initiative.initiative
            .toLowerCase()
            .includes(filters.initiative.toLowerCase())) &&
        (filters.branch === "" || initiative.branch === filters.branch) &&
        (filters.workfront === "" ||
          initiative.workfront === filters.workfront) &&
        (filters.leader === "" || initiative.leader === filters.leader || initiative.leader2 === filters.leader) &&
        (filters.tag === "" || initiative.tags?.includes(filters.tag))
      );
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      const aValue = a[sortConfig.key].toString().toLowerCase();
      const bValue = b[sortConfig.key].toString().toLowerCase();
      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });

  // ######====== HANDLE FOR INITIATIVE CLICKING (SET SELECTED INITIATIVE, LOAD DETAILS, ETC)
  // When clicking on the initiative, the initiative object is saved in selectedInitiative
  const handleInitiativeClick = async (id, targetSection = "details", isBackgroundRefresh = false) => {
    const startTime = performance.now();
    
    try {
      // Só mostrar loader se não for refresh em background
      if (!isBackgroundRefresh) {
        setIsLoadingDetails(true); // Show loader
        setSelectedInitiative(null); // Clear any previous data
      }
      
      // OPTIMIZATION: Set a minimal initiative object immediately to trigger other useEffects
      const tempInitiative = { id: id, initiative: "Loading..." };

      setSelectedInitiative(tempInitiative);
      setSelectedSection(targetSection);
      setIsTableCollapsed(true);
      
      // SUPER OPTIMIZATION: If target is one_page, start fetching OnePage data in parallel
      let onePagePromise = null;
      if (targetSection === "one_page") {

        onePagePromise = fetchOnePageData(id);
      }
      
      const response = await client.post("/api/initiative-detail/", {
        action: "detail",
        initiative_id: id,
      });

      if (response.data && response.data.success) {
        const endTime = performance.now();

        
        // The backend returns an object: { success: true, initiative: { ... } }
        const initiativeData = response.data.initiative;
        const metadata = response.data.customFieldsMetadata; // NOVO
        
        // Dados da iniciativa recebidos com sucesso
        
        // Update with full data
        setSelectedInitiative(initiativeData);
        setSelectedInitiativeCustomFields(metadata); // Guarde num state
        
        // Só parar o loader se não for background refresh
        if (!isBackgroundRefresh) {
          setIsLoadingDetails(false);
        }
        
        // Wait for OnePage data if it was started in parallel
        if (onePagePromise) {
          console.warn('⚡ [PARALLEL] Waiting for OnePage data...');
          await onePagePromise;
          console.warn('✅ [PARALLEL] OnePage data ready!');
        }
      } else {
        const endTime = performance.now();
        console.error(`❌ Initiative load failed after ${(endTime - startTime).toFixed(2)}ms`);
        setSelectedInitiative(null); // Clear temp data on error
        setIsLoadingDetails(false);
      }
    } catch (error) {
      const endTime = performance.now();
      console.log(`❌ Initiative load error after ${(endTime - startTime).toFixed(2)}ms:`, error);
      setSelectedInitiative(null); // Clear temp data on error
      
      // Só parar o loader se não for background refresh
      if (!isBackgroundRefresh) {
        setIsLoadingDetails(false);
      }
    }
  };

  // Helper para verificar se um drop é permitido
  const isDropAllowed = (initiativeStage, targetStage) => {
    // Se for o mesmo estágio, não permite (não faz sentido)
    if (initiativeStage === targetStage) return false;
    
    // Encontra os índices dos estágios
    const currentIndex = stages.indexOf(initiativeStage);
    const targetIndex = stages.indexOf(targetStage);
    
    if (currentIndex === -1 || targetIndex === -1) return false;
    
    // TODO: Se o usuário é admin (aprovador), permite qualquer mudança
    // Por enquanto, vamos aplicar a regra restritiva para todos
    // No futuro, adicionar: if (userDetails.is_stage_change_admin) return true;
    
    // Usuários normais: só podem avançar 1 estágio, não podem voltar
    if (targetIndex < currentIndex) return false; // Não pode voltar
    if (targetIndex > currentIndex + 1) return false; // Não pode pular
    
    return true; // Pode avançar para o próximo
  };

  const onDragStartInitiatives = (start) => {
    const initiativeId = start.draggableId;
    const initiative = initiatives.find((i) => i.id === initiativeId);
    setDraggingInitiative(initiative);
  };

  const onDragEndInitiatives = async (result) => {
    setDraggingInitiative(null); // Limpa o estado de drag
    
    if (!userDetails?.permissions?.can_change_initiative_stage) {
      return;
    }
    if (!result.destination) return;

    const initiativeId = result.draggableId;
    const newStage = result.destination.droppableId;
    const sourceStage = result.source.droppableId;

    // Se não mudou de estágio, não faz nada
    if (sourceStage === newStage) return;

    // ========== UPDATE OTIMISTA ==========
    // 1. Mostra loading imediatamente
    setLoadingInitiativeId(initiativeId);
    
    // 2. Atualiza estado local ANTES da API (para mostrar HourglassEmpty)
    const updated = [...initiatives];
    const idx = updated.findIndex((i) => i.id === initiativeId);
    const previousInitiativeState = idx !== -1 ? { ...updated[idx] } : null;
    
    if (idx !== -1) {
      updated[idx] = { 
        ...updated[idx], 
        stage: newStage,
        approval_status: "pending" // Marca como pendente imediatamente
      };
      setInitiatives(updated);
    }

    // 3. Chama API para salvar
    try {
      await client.post("/api/update-initiative-stage/", {
        initiative_id: initiativeId,
        new_stage: newStage,
      });
      
      // Sucesso: recarrega aprovações
      reloadApprovalsData();
      toast.success(t("initiatives.stage_updated_successfully", "Estágio atualizado com sucesso!"));
    } catch (error) {
      // ========== ROLLBACK EM CASO DE ERRO ==========
      // Reverte para o estado anterior
      if (idx !== -1 && previousInitiativeState) {
        const rolledBack = [...initiatives];
        rolledBack[idx] = previousInitiativeState;
        setInitiatives(rolledBack);
      }
      
      // Tratamento específico de erros
      const errorCode = error?.response?.data?.error;
      const errorMessage = error?.response?.data?.message;
      
      if (errorCode === "cannot_go_back") {
        toast.error(
          errorMessage || 
          "Você não pode voltar estágios. Apenas aprovadores podem fazer isso."
        );
      } else if (errorCode === "cannot_skip_stages") {
        toast.error(
          errorMessage || 
          "Você não pode pular estágios. Avance apenas para o próximo estágio sequencial."
        );
      } else if (errorCode === "invalid_stage") {
        toast.error(errorMessage || "Estágio inválido.");
      } else {
        toast.error(
          errorMessage || 
          t("errors.failed_to_move_initiative", "Não foi possível mover a iniciativa. Tente novamente.")
        );
      }
      
      // Força reload completo para garantir consistência
      await loadInitiativesData();
    } finally {
      setLoadingInitiativeId(null);
    }
  };

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  function renderSelectedInitiativeDiv() {
    if (!selectedInitiative) return null;

    const isSelected = (section) => selectedSection === section;

    const navColor = isSmall
      ? "#3C4858" // texto/preto em mobile, por exemplo
      : "#fff";

    return (
      <div className={classes.floatingCard}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%", // para ocupar toda a largura do card
          }}
        >
          {/* Initiative name */}
          <div
            style={{
              fontSize: "14px",
              fontWeight: 500,
              color: navColor,
              marginRight: "10px",
              display: "flex",
              alignItems: "center",
              minHeight: "20px",
            }}
          >
            {selectedInitiative.initiative === "Loading..." ? (
              <ThreeDots
                color="white"
                height={14}
                width={14}
                ariaLabel="loading"
              />
            ) : (
              selectedInitiative.initiative
            )}
          </div>

          {/* Top page Buttons */}
          <div style={{ display: "flex", gap: "10px" }}>
            <Tooltip title={t("initiatives.details")}>
              <Visibility
                className={classes.iconButtonCustom}
                onClick={() => setSelectedSection("details")}
                style={{
                  cursor: "pointer",
                  fontSize: "20px",
                  color: navColor,
                  borderBottom: isSelected("details")
                    ? `2px solid ${navColor}`
                    : "none",
                }}
              />
            </Tooltip>
            {userDetails.has_approvals_stages && (
              <Tooltip title={t("initiatives.approvals_icon")}>
                <ChecklistRtlIcon
                  className={classes.iconButtonCustom}
                  onClick={() => setSelectedSection("approvals")}
                  style={{
                    cursor: "pointer",
                    color: navColor,
                    fontSize: "20px",
                    borderBottom: isSelected("approvals")
                      ? `2px solid ${navColor}`
                      : "none",
                  }}
                />
              </Tooltip>
            )}
            {userDetails.has_op_info && (
              <Tooltip title={t("initiatives.one_page_icon")}>
                <ViewQuilt
                  className={classes.iconButtonCustom}
                  onClick={() => setSelectedSection("one_page")}
                  style={{
                    cursor: "pointer",
                    color: navColor,
                    fontSize: "23px",
                    borderBottom: isSelected("one_page")
                      ? `2px solid ${navColor}`
                      : "none",
                  }}
                />
              </Tooltip>
            )}
            {userDetails.has_deliverables_info && (
              <Tooltip title={t("initiatives.plan_icon")}>
                <Event
                  className={classes.iconButtonCustom}
                  onClick={() => setSelectedSection("execution")}
                  style={{
                    cursor: "pointer",
                    color: navColor,
                    fontSize: "20px",
                    borderBottom: isSelected("execution")
                      ? `2px solid ${navColor}`
                      : "none",
                  }}
                />
              </Tooltip>
            )}
            {userDetails.has_results_info && (
              <Tooltip title={t("initiatives.results_icon")}>
                <AttachMoney
                  className={classes.iconButtonCustom}
                  onClick={() => setSelectedSection("results")}
                  style={{
                    cursor: "pointer",
                    color: navColor,
                    fontSize: "20px",
                    borderBottom: isSelected("results")
                      ? `2px solid ${navColor}`
                      : "none",
                  }}
                />
              </Tooltip>
            )}
            {userDetails?.permissions?.can_edit_initiatives && (
              <Tooltip title={t("initiatives.edit_icon")}>
                <Edit
                  className={classes.iconButtonCustom}
                  onClick={() => {
                    setInitiativeToEdit(selectedInitiative);
                    setIsDrawerOpen(true);
                    setSelectedSection("edit");
                  }}
                  style={{
                    cursor: "pointer",
                    fontSize: "17px",
                    color: navColor,
                    borderBottom: isSelected("edit")
                      ? `2px solid ${navColor}`
                      : "none",
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
    );
  }

  // helpers/selectOptions.js (ou dentro do mesmo arquivo)
  const buildOptions = (arr) => Array.from(new Set(arr.filter(Boolean))); // remove vazios, deduplica

  // Helper para formatar os líderes
  const formatLeadersNames = (leader, leader2) => {
    const leaders = [leader, leader2].filter(Boolean);
    return leaders.join(", ");
  };

  const renderAdvancedFilters = () => (
    <GridContainer spacing={2} style={{ flex: 1 }}>
      {/* BRANCH ------------------------------------------------------- */}
      <GridItem xs={6} md={3}>
        <FormControl
          fullWidth
          className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
        >
          <Select
            value={filters.branch}
            onChange={(e) => handleFilterChange("branch", e.target.value)}
            displayEmpty
            classes={{
              select: `${classes.select} ${classes.selectFormControlOverride}`,
            }}
          >
            <MenuItem
              value=""
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {getAltLabel("filters.all_branches", userDetails, t)}
            </MenuItem>

            {buildOptions(initiatives.map((i) => i.branch)).map((branch) => (
              <MenuItem
                key={branch}
                value={branch}
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {branch}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridItem>

      {/* WORKFRONT ---------------------------------------------------- */}
      <GridItem xs={6} md={3}>
        <FormControl
          fullWidth
          className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
        >
          <Select
            value={filters.workfront}
            onChange={(e) => handleFilterChange("workfront", e.target.value)}
            displayEmpty
            classes={{
              select: `${classes.select} ${classes.selectFormControlOverride}`,
            }}
          >
            <MenuItem
              value=""
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {getAltLabel("filters.all_workfronts", userDetails, t)}
            </MenuItem>

            {buildOptions(initiatives.map((i) => i.workfront)).map((wf) => (
              <MenuItem
                key={wf}
                value={wf}
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {wf}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridItem>

      {/* LEADER ------------------------------------------------------- */}
      <GridItem xs={6} md={3}>
        <FormControl
          fullWidth
          className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
        >
          <Select
            value={filters.leader}
            onChange={(e) => handleFilterChange("leader", e.target.value)}
            displayEmpty
            classes={{
              select: `${classes.select} ${classes.selectFormControlOverride}`,
            }}
          >
            <MenuItem
              value=""
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {t("filters.all_leaders")}
            </MenuItem>

            {buildOptions(initiatives.flatMap((i) => [i.leader, i.leader2].filter(Boolean))).map((leader) => (
              <MenuItem
                key={leader}
                value={leader}
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {leader}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </GridItem>

      {/* TAG ---------------------------------------------------------- */}
      <GridItem xs={6} md={3}>
        <FormControl
          fullWidth
          className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
        >
          <Select
            value={filters.tag}
            onChange={(e) => handleFilterChange("tag", e.target.value)}
            displayEmpty
            classes={{
              select: `${classes.select} ${classes.selectFormControlOverride}`,
            }}
          >
            <MenuItem
              value=""
              classes={{
                root: classes.selectMenuItem,
                selected: classes.selectMenuItemSelected,
              }}
            >
              {getAltLabel("filters.all_tags", userDetails, t)}
            </MenuItem>

            {buildOptions(initiatives.flatMap((i) => i.tags || [])).map(
              (tag) => (
                <MenuItem
                  key={tag}
                  value={tag}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {tag}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>
      </GridItem>
    </GridContainer>
  );

  function renderCustomFieldsDetail() {
    if (!selectedInitiative || !selectedInitiative.customFields) return null;

    /**  Tamanho dinâmico: tipos "longos" ocupam a linha toda  */
    const getGridSizes = (type) => {
      const fullWidthTypes = ["multichoices", "text"];
      return fullWidthTypes.includes(type)
        ? { xs: 12, sm: 12, md: 12 } // ocupa 100
        : { xs: 12, sm: 6, md: 4 }; // divide em 2‒3 colunas
    };

    return selectedInitiativeCustomFields.map((field) => {
      const { field_name, field_label, field_type, choices = [] } = field;
      const storedValue = selectedInitiative.customFields[field_name] ?? "";

      /* tamanhos calculados */
      const sizeProps = getGridSizes(field_type);

      switch (field_type) {
        /* ───────────── TEXTOS SIMPLES ───────────── */
        case "text":
        case "char":
        case "number":
        case "date":
          return (
            <GridItem
              {...sizeProps}
              key={field_name}
              style={{ marginBottom: 16 }}
            >
              <TextField
                fullWidth
                label={field_label}
                name={field_name}
                type={field_type === "number" ? "number" : field_type}
                value={storedValue}
                disabled
                InputProps={{
                  readOnly: true,
                  classes: { input: classes.inputText },
                }}
                InputLabelProps={{
                  classes: { root: classes.labelText },
                  shrink: true,
                }}
                classes={{ root: classes.textFieldRoot }}
              />
            </GridItem>
          );

        /* ───────────── LISTA DE OPÇÕES ÚNICA ───────────── */
        case "choice":
          return (
            <GridItem
              {...sizeProps}
              key={field_name}
              style={{ marginBottom: 16 }}
            >
              <TextField
                select
                fullWidth
                label={field_label}
                name={field_name}
                value={storedValue}
                disabled
                InputProps={{ readOnly: true }}
                InputLabelProps={{ classes: { root: classes.labelText } }}
                classes={{ root: classes.textFieldRoot }}
              >
                {choices.map((opt, idx) => (
                  <MenuItem
                    key={idx}
                    value={opt}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </GridItem>
          );

        /* ───────────── MULTI‑CHOICES (TAGs) ───────────── */
        case "multichoices": {
          const values = storedValue
            .toString()
            .split(";")
            .map((v) => v.trim())
            .filter(Boolean);

          return (
            <GridItem
              {...sizeProps}
              key={field_name}
              style={{ marginBottom: 16 }}
            >
              <div className={classes.multiSelectContainer}>
                <label
                  className={classes.labelMultiSelect}
                  style={{ marginBottom: 8 }}
                >
                  {field_label}
                </label>
                <ReactSelect
                  isMulti
                  isDisabled
                  value={values.map((v) => ({ label: v, value: v }))}
                  styles={customStyles}
                  placeholder={t("new_initiatives.fields.select_label")}
                />
              </div>
            </GridItem>
          );
        }

        /* ───────────── BOOLEAN (SWITCH) ───────────── */
        case "boolean":
          return (
            <GridItem
              {...sizeProps}
              key={field_name}
              style={{ marginBottom: 16 }}
            >
              <div className={classes.switchContainer}>
                <Typography className={classes.labelSwitch}>
                  {field_label}
                </Typography>
                <Switch
                  checked={storedValue === "true" || storedValue === true}
                  disabled
                  name={field_name}
                />
              </div>
            </GridItem>
          );

        default:
          return null;
      }
    });
  }

  const getSummaryData = () => {
    const summaryConfig = summaryCardsLayout;
    if (summaryConfig.length === 0 || sortedAndFilteredInitiatives.length === 0) return [];

    // console.log("🔍 [DEBUG] getSummaryData - Configuração:", summaryConfig);
    // console.log("🔍 [DEBUG] getSummaryData - Iniciativas filtradas:", sortedAndFilteredInitiatives.length);
    // console.log("🔍 [DEBUG] getSummaryData - Exemplo de iniciativa:", sortedAndFilteredInitiatives[0]);

    const initialValues = summaryConfig.reduce((acc, card) => {
      if (card.key.startsWith("sum_")) {
          acc[card.key] = 0;
      }
      return acc;
    }, { total_initiatives: sortedAndFilteredInitiatives.length });

    // console.log("🔍 [DEBUG] getSummaryData - Valores iniciais:", initialValues);

    const sums = sortedAndFilteredInitiatives.reduce((acc, initiative) => {
        summaryConfig.forEach(card => {
            if (card.key.startsWith("sum_")) {
                const field = card.key.replace("sum_", "");
                // Para readiness_percent, não somar - será calculado separadamente
                if (card.key === "sum_readiness_percent") {
                    return; // Pular este campo, será calculado depois
                }
                const value = initiative[field] || 0;
                acc[card.key] += value;
                
                // Log específico para deliverables
                if (card.key.includes("deliverables")) {
                  // console.log(`🔍 [DEBUG] ${card.key}: iniciativa ${initiative.initiative} tem ${field} = ${value}, acumulado = ${acc[card.key]}`);
                }
            } else if (card.key.startsWith("avg_")) {
                // Para campos de média (como readiness), somar os valores para calcular média depois
                const field = card.key.replace("avg_", "");
                if (!acc[card.key + "_sum"]) acc[card.key + "_sum"] = 0;
                if (!acc[card.key + "_count"]) acc[card.key + "_count"] = 0;
                
                const value = initiative[field] || 0;
                acc[card.key + "_sum"] += value;
                acc[card.key + "_count"] += 1;
                
                // Log específico para readiness
                if (card.key.includes("readiness")) {
                  // console.log(`🔍 [DEBUG] ${card.key}: iniciativa ${initiative.initiative} tem ${field} = ${value}, soma = ${acc[card.key + "_sum"]}, count = ${acc[card.key + "_count"]}`);
                }
            }
        });
        return acc;
    }, initialValues);

    // console.log("🔍 [DEBUG] getSummaryData - Somas calculadas:", sums);

    // Calcular percent_captured para KPIs (se existir)
    if (sums.sum_kpi_planned_total > 0) {
      sums.percent_captured = (sums.sum_kpi_actual / sums.sum_kpi_planned_total) * 100;
    } else {
      sums.percent_captured = 0;
    }

    // Calcular percent_readiness para deliverables (se existir)
    if (sums.sum_deliverables_planned > 0) {
      sums.sum_readiness_percent = (sums.sum_deliverables_completed / sums.sum_deliverables_planned) * 100;
      // console.log("🔍 [DEBUG] Readiness calculado:", {
      //   completed: sums.sum_deliverables_completed,
      //   planned: sums.sum_deliverables_planned,
      //   percent: sums.sum_readiness_percent,
      //   calculation: `${sums.sum_deliverables_completed} / ${sums.sum_deliverables_planned} * 100 = ${sums.sum_readiness_percent}`
      // });
    } else {
      sums.sum_readiness_percent = 0;
      // console.log("🔍 [DEBUG] Readiness zerado - sem deliverables planejados");
    }

    // Calcular médias de readiness MVP e Launch
    summaryConfig.forEach(card => {
        if (card.key.startsWith("avg_")) {
            const countKey = card.key + "_count";
            const sumKey = card.key + "_sum";
            
            if (sums[countKey] && sums[countKey] > 0) {
                sums[card.key] = sums[sumKey] / sums[countKey];
                // console.log(`🔍 [DEBUG] ${card.key} calculado: ${sums[sumKey]} / ${sums[countKey]} = ${sums[card.key]}`);
            } else {
                sums[card.key] = 0;
                // console.log(`🔍 [DEBUG] ${card.key} zerado - sem dados`);
            }
        }
    });
    
    return summaryConfig.map(card => ({
        label: getAltLabel(card.label_key, userDetails, t),
        value: (() => {
            const val = sums[card.key];
            switch (card.type) {
                case "count":
                    return sums.total_initiatives;
                case "number_sum":
                    // Para deliverables, não usar unidade (são contadores, não valores monetários)
                    if (card.key.includes("deliverables")) {
                        return formatNumber(val || 0, "", false); // Sem unidade para deliverables
                    }
                    // Para KPIs, usar a unidade da iniciativa
                    return formatNumber(val || 0, sortedAndFilteredInitiatives[0]?.kpi_unit || "", true);
                case "percentage_avg":
                    // Para readiness, usar o valor calculado
                    if (card.key === "sum_readiness_percent") {
                        const readinessVal = sums.sum_readiness_percent || 0;
                        // console.log("🔍 [DEBUG] Renderizando readiness:", readinessVal);
                        return `${readinessVal.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
                    }
                    // Para campos de média de readiness (MVP e Launch)
                    if (card.key.startsWith("avg_") && card.key.includes("readiness")) {
                        const avgVal = sums[card.key] || 0;
                        // console.log(`🔍 [DEBUG] Renderizando ${card.key}:`, avgVal);
                        return `${avgVal.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
                    }
                    // Para outros percentuais
                    const numVal = typeof val === 'number' && !isNaN(val) ? val : 0;
                    return `${numVal.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
                default:
                    return val || 0;
            }
        })()
    }));
  };

  const summaryData = getSummaryData(); // Call it once

  // coloque antes do return do componente
  const secureDownload = async (blobName, suggestedName) => {
    try {
      await client.post("/api/awake/");
      const { data } = await client.post(
        "/api/download_initiative_file/",
        { blob_name: blobName },
        { responseType: "blob" }
      );
      saveAs(new Blob([data]), suggestedName);
    } catch (err) {
      // console.error("Download error:", err);
    }
  };

  const [loadingDownloads, setLoadingDownloads] = useState({});

  const handleDownload = async (blobName, fileName) => {
    if (loadingDownloads[blobName]) return; // já baixando
    setLoadingDownloads((p) => ({ ...p, [blobName]: true }));

    try {
      await secureDownload(blobName, fileName); // função já recebida por prop
    } finally {
      setLoadingDownloads((p) => ({ ...p, [blobName]: false }));
    }
  };

  /* ------------------------------------------------------------------
   Somatórios por estágio (usado no Board View) - DINÂMICO baseado na configuração
------------------------------------------------------------------ */
  
  // Buscar o card marcado para aparecer no board view
  const boardCard = summaryCardsLayout.find(card => card.show_in_board === 1.0);
  const boardFieldKey = boardCard ? boardCard.key.replace(/^(sum_|avg_)/, "") : "kpi_planned_total";
  const boardIsSum = boardCard ? boardCard.key.startsWith("sum_") : true;
  const boardIsAvg = boardCard ? boardCard.key.startsWith("avg_") : false;
  
  const stageSummary = stages.reduce((acc, stageKey) => {
    const items = sortedAndFilteredInitiatives.filter(
      (ini) => ini.stage === stageKey
    );

    const sum = items.reduce(
      (tot, ini) => {
        // Campos fixos sempre calculados
        tot.kpi_actual += ini.kpi_actual || 0;
        tot.kpi_planned_total += ini.kpi_planned_total || 0;
        tot.kpi_target += ini.kpi_annual_value_target || 0;
        tot.count += 1;
        
        // Campo dinâmico baseado na configuração
        if (boardIsSum || boardIsAvg) {
          const fieldValue = ini[boardFieldKey] || 0;
          if (!tot.dynamic_sum) tot.dynamic_sum = 0;
          if (!tot.dynamic_count) tot.dynamic_count = 0;
          tot.dynamic_sum += fieldValue;
          tot.dynamic_count += 1;
        }
        
        return tot;
      },
      { 
        kpi_actual: 0, 
        kpi_planned_total: 0, 
        kpi_target: 0, 
        count: 0,
        dynamic_sum: 0,
        dynamic_count: 0
      }
    );

    // Calcular o valor dinâmico final
    if (boardIsAvg && sum.dynamic_count > 0) {
      sum.dynamic_value = sum.dynamic_sum / sum.dynamic_count;
    } else {
      sum.dynamic_value = sum.dynamic_sum;
    }

    sum.percent =
      sum.kpi_planned_total > 0
        ? (sum.kpi_actual / sum.kpi_planned_total) * 100
        : 0;

    acc[stageKey] = sum;
    return acc;
  }, {});

  const renderKpis = () => {
    const initiativeKpis = selectedInitiative.selectedKpis || [];

    if (initiativeKpis.length === 0) {
      return (
        <GridItem xs={12}>
          <Typography variant="body2" style={{ color: "#999", marginTop: 10 }}>
            {t("initiatives.no_kpis_found")}
          </Typography>
        </GridItem>
      );
    }

    /* quebro em grupos de 3 para responsividade */
    const groups = [];
    for (let i = 0; i < initiativeKpis.length; i += 3) {
      groups.push(initiativeKpis.slice(i, i + 3));
    }

    return groups.map((group, gIdx) => {
      const colSize = group.length === 1 ? 12 : group.length === 2 ? 6 : 4;

      return (
        <GridContainer spacing={2} key={`kpi-group-${gIdx}`}>
          {group.map((kpi, idx) => {
            const formattedTarget =
              kpi.annual_value != null && !isNaN(kpi.annual_value)
                ? Number(kpi.annual_value).toLocaleString()
                : "N/A";

            return (
              <GridItem xs={12} md={colSize} key={`kpi-${gIdx}-${idx}`}>
                <div
                  style={{
                    marginBottom: 12,
                    backgroundColor: "#f9f9f9",
                    padding: "8px 12px",
                    borderRadius: 4,
                  }}
                >
                  <div style={{ marginBottom: 4, fontSize: 14 }}>
                    <strong>{t("initiatives.kpi")}:</strong> {kpi.kpi_name}
                  </div>
                  <div style={{ marginBottom: 4, fontSize: 14 }}>
                    <strong>{t("initiatives.kpi_type")}:</strong>{" "}
                    {kpi.kpi_type === "program"
                      ? t("results_kpi.program_tab")
                      : t("results_kpi.initiative_tab")}
                  </div>
                  <div style={{ marginBottom: 4, fontSize: 14 }}>
                    <strong>{t("initiatives.kpi_subtype")}:</strong>{" "}
                    {t(`initiatives.${kpi.kpi_subtype}`)}
                  </div>
                  <div style={{ fontSize: 14 }}>
                    <strong>
                      {getAltLabel(
                        "initiatives.results_target",
                        userDetails,
                        t
                      )}
                      :
                    </strong>{" "}
                    {formattedTarget}
                  </div>
                  {/* ▼  Arquivos anexados a ESTE KPI  ▼ */}
                  {kpi.files && kpi.files.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Typography variant="caption" style={{ fontWeight: 600 }}>
                        {t("initiative_selected.files")}:
                      </Typography>

                      {kpi.files.map((file, fIdx) => (
                        <div
                          key={fIdx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            marginTop: 4,
                            padding: "4px 6px",
                            borderRadius: 4,
                            border: `1px solid ${clientColor}20`,
                          }}
                        >
                          <Typography
                            variant="caption"
                            style={{
                              flex: 1,
                              minWidth: 0,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                            title={file.file_name}
                          >
                            {file.file_name}
                          </Typography>
                          <Tooltip title={t("initiative_selected.download")}>
                            <Button
                              justIcon
                              color="client"
                              className={classes.cardButton}
                              disabled={loadingDownloads[file.blob_name]}
                              onClick={() =>
                                handleDownload(file.blob_name, file.file_name)
                              }
                              style={{
                                transform: "translateY(-8px)",
                              }}
                            >
                              {loadingDownloads[file.blob_name] ? (
                                <ThreeDots
                                  color="white"
                                  height={18}
                                  width={18}
                                  ariaLabel="loading"
                                />
                              ) : (
                                <CloudDownloadIcon
                                  className={classes.cardButtonIcon}
                                />
                              )}
                            </Button>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </GridItem>
            );
          })}
        </GridContainer>
      );
    });
  };

  const StageNumber = ({ label, value, grow = false }) => (
    <div
      style={{
        flex: grow ? 1 : "unset", // permite dividir espaço na linha
        textAlign: "center",
        minWidth: 80, // evita ficar muito estreito
      }}
    >
      <Typography
        variant="caption"
        style={{ color: "#3C4858", fontWeight: 300, fontSize: 12 }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle2"
        style={{ color: "#3C4858", fontWeight: 500, fontSize: 15 }}
      >
        {value}
      </Typography>
    </div>
  );

  StageNumber.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    grow: PropTypes.bool,
  };

  StageNumber.defaultProps = {
    grow: false,
  };

  const summary = getSummaryData();

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedSection("details"); // garante que sempre volta p/ Details
  };

  // Componente moderno para Summary Cards
  const ModernSummaryCard = ({ item, index, clientColor }) => {
    const cardStyles = {
      background: "white",
      borderRadius: "8px",
      padding: "12px 8px",
      textAlign: "center",
      position: "relative",
      overflow: "hidden",
      border: `1px solid ${clientColor}15`,
      minHeight: "70px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    };

    const accentBarStyles = {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      background: clientColor,
      borderRadius: "8px 8px 0 0",
    };

    const labelStyles = {
      fontSize: "0.7rem",
      fontWeight: "500",
      color: "#6b7280",
      marginBottom: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.3px",
      lineHeight: "1.2",
    };

    const valueStyles = {
      fontSize: "1.3rem",
      fontWeight: "600",
      color: clientColor,
      lineHeight: "1.1",
      margin: 0,
    };


    return (
      <div
        style={cardStyles}
      >
        <div style={accentBarStyles} />
        <div style={labelStyles}>{item.label}</div>
        <div style={valueStyles}>{item.value}</div>
      </div>
    );
  };

  return (
    <>
      <div>{renderSelectedInitiativeDiv()}</div>
      <ApprovalsFloating
        t={t}
        classes={classes}
        clientColor={clientColor}
        approvalsData={approvalsData}
        approvalsLoaded={approvalsLoaded}
        onInitiativeClick={(id) => handleInitiativeClick(id)}
        onReloadApprovals={reloadApprovalsData}
        onReloadInitiatives={fetchInitiatives}
      />
      <GridContainer
        style={{
          marginTop: selectedInitiative ? -40 : -40,
          marginBottom: "25px",
        }}
      >
        <GridItem xs={12} style={{ margin: 0 }}>
          <Card style={{ marginBottom: 0 }}>
            <CardHeader color="client" icon>
              <CardIcon color="client">
                <Assignment />
              </CardIcon>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <h4
                  className={classes.cardIconTitle}
                  style={{ marginBottom: "18px", display: "inline-flex" }}
                >
                  {getAltLabel("initiatives.initiative_list", userDetails, t)}
                </h4>
                <div style={{ padding: "1px 40px 5px 0px" }}>
                  {selectedInitiative && (
                    <IconButton
                      onClick={() => {
                        const newCollapsed = !isTableCollapsed;
                        setIsTableCollapsed(newCollapsed);
                        // Se a tabela está sendo expandida (newCollapsed é false), recarregue as iniciativas
                        if (!newCollapsed) {
                          fetchInitiatives();
                        }
                      }}
                      size="small"
                    >
                      {isTableCollapsed ? (
                        <ExpandMoreIcon
                          style={{ height: "20px", width: "20px" }}
                        />
                      ) : (
                        <ExpandLessIcon
                          style={{ height: "20px", width: "20px" }}
                        />
                      )}
                    </IconButton>
                  )}
                </div>
              </div>
            </CardHeader>
            {isLoading || isLoadingDetails ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100vh",
                }}
              >
                <ThreeDots
                  color={userDetails.implementation_color}
                  height={80}
                  width={80}
                  ariaLabel="loading"
                />
              </div>
            ) : (
              <>
                <GridContainer
                  justify="flex-end"
                  style={{
                    position: "absolute",
                    top: "1px",
                  }}
                >
                  <GridItem
                    style={{
                      position: "absolute",
                      right: "35px",
                      top: "10px",
                    }}
                  >
                    {isLoading && (
                      <ThreeDots
                        color={userDetails.implementation_color}
                        height={22}
                        width={22}
                        ariaLabel="loading"
                      />
                    )}
                  </GridItem>
                  <GridItem
                    style={{
                      zIndex: 1000,
                    }}
                  >
                    {userDetails?.permissions?.can_create_initiatives && (
                      <Tooltip title={getAltLabel("initiatives.add_new", userDetails, t)}>
                        <Button
                          justIcon
                          color="client"
                          className={classes.cardButton}
                          onClick={() => {
                            setInitiativeToEdit(null);
                            setIsDrawerOpen(true);
                          }}
                        >
                          <AddCircle className={classes.cardButtonIcon} />
                        </Button>
                      </Tooltip>
                    )}
                  </GridItem>
                </GridContainer>

                {/* Drawer to add new initiative */}
                <Drawer
                  anchor="right"
                  open={isDrawerOpen}
                  onClose={handleCloseDrawer}
                  classes={{ paper: classes.drawerPaper }}
                  BackdropProps={{
                    style: { backgroundColor: "#000", opacity: "0.1" },
                  }}
                  PaperProps={{
                    style: {
                      borderRadius: "10px",
                      margin: "1vh 1vh 1vh 0",
                      height: "98vh",
                    },
                  }}
                >
                  <NewInitiativeForm
                    initiativeToEdit={initiativeToEdit}
                    onCloseDrawer={handleCloseDrawer}
                    onReloadInitiatives={reloadInitiativesWrapper}
                  />
                </Drawer>
                <div
                  style={{
                    maxHeight: isTableCollapsed ? "0px" : "9999px",
                    opacity: isTableCollapsed ? 0 : 1,
                    transition: "max-height 0.5s ease, opacity 0.5s ease", // tempo menor
                    overflow: "hidden",
                  }}
                >
<GridContainer
  style={{
    marginTop: "-10px",
    marginBottom: "7px",
    padding: "8px",
    justifyContent: "space-between",
    gap: "8px",
  }}
>
  {/* ENGLISH: Dynamically render modern summary cards based on the configuration */}
  {summaryData.map((item, idx) => (
    <GridItem
      xs={6}
      sm={4}
      md={3}
      lg={2}
      key={idx}
      style={{ paddingBottom: "4px" }}
    >
      <ModernSummaryCard 
        item={item} 
        index={idx} 
        clientColor={clientColor} 
      />
    </GridItem>
  ))}
</GridContainer>

                  {/* Seção de Filtros */}
                  <div
                    style={{
                      padding: "0 16px 0px 16px",
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      //gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        marginRight: "-10px",
                      }}
                    >
                      <Tooltip title={t("filters.clear_all")}>
                        <FontAwesomeIcon
                          onClick={() =>
                            setFilters({
                              status: "",
                              initiative: "",
                              branch: "",
                              workfront: "",
                              leader: "",
                              tag: "",
                            })
                          }
                          icon={faFilter}
                          style={{
                            marginRight: "16px",
                            fontSize: "20px",
                            color: "#666",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                      <div className={classes.clearFilters}>X</div>
                    </div>
                    <GridItem xs={12} md={4} style={{ flex: 1 }}>
                      <div style={{ padding: "0px 3px 7px 0px" }}>
                        <TextField
                          label={
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              {getAltLabel("filters.initiatives", userDetails, t)}
                              <Search className={classes.searchIcon} />
                            </div>
                          }
                          size="small"
                          classes={{ root: classes.textFieldRoot }}
                          InputProps={{
                            classes: { input: classes.inputText },
                            style: { fontSize: "12px" },
                          }}
                          InputLabelProps={{
                            classes: { root: classes.labelText },
                            shrink: true,
                          }}
                          value={filters.initiative}
                          onChange={(e) =>
                            handleFilterChange("initiative", e.target.value)
                          }
                          style={{ width: "100%" }}
                        />
                      </div>
                    </GridItem>
                    {!isSmall && (
                      <div style={{ flex: 1, marginTop: "8px" }}>
                        {renderAdvancedFilters()}
                      </div>
                    )}
                    {/* Se FOR mobile, exibimos o botão + advanced filters na mesma área */}
                    {isSmall && (
                      <GridItem
                        xs={12}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginTop: "2px",
                          gap: "8px",
                          flexWrap: "wrap",
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <div
                          style={{
                            flexShrink: 0, // não diminui
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Tooltip
                            title={
                              showAdvancedFilters
                                ? t("filters.close_advanced_filters") // Texto para "fechar"
                                : t("filters.open_advanced_filters") // Texto para "abrir"
                            }
                            arrow
                          >
                            <Button
                              disableRipple
                              className={classes.expandAdvancedFilters}
                              onClick={() =>
                                setShowAdvancedFilters(!showAdvancedFilters)
                              }
                              style={{ flexShrink: 0 }}
                            >
                              <FilterListIcon />
                            </Button>
                          </Tooltip>
                        </div>

                        {/* Ao lado do botão, se showAdvancedFilters = true, exibe */}
                        {showAdvancedFilters && (
                          <div
                            style={{
                              display: "inline-block",
                              gap: "8px",
                              flexWrap: "wrap",
                              flex: 1,
                              paddingRight: "3px",
                              marginLeft: "-10px",
                            }}
                          >
                            {renderAdvancedFilters()}
                          </div>
                        )}
                      </GridItem>
                    )}
                  </div>
                  <div style={{ margin: "10px" }}>
                    <Tabs
                      value={initiativesView}
                      onChange={(e, newValue) => setInitiativesView(newValue)}
                      indicatorColor="primary"
                      textColor="primary"
                      classes={{
                        root: classes.tabsRoot,
                        indicator: classes.indicator,
                      }}
                      variant="fullWidth"
                    >
                      <Tab
                        label={t("initiatives.table_view")}
                        value="table"
                        classes={{ root: classes.tabRoot }}
                      />
                      <Tab
                        label={t("initiatives.board_view")}
                        value="board"
                        classes={{ root: classes.tabRoot }}
                      />
                      <Tab
                        label={t("initiatives.gantt_view")}
                        value="gantt"
                        classes={{ root: classes.tabRoot }}
                      />
                    </Tabs>
                  </div>

                  {/* Table Section */}
                  {initiativesView === "table" && (
                    <CardBody>
                      <div style={{ overflowX: "auto" }}>
                        <TableContainer>
                          <Table stickyHeader>
<TableHead>
  <TableRow>
    {/* ENGLISH: Dynamically generate table headers from config */}
    {(tableLayout.columns || []).map((col) => (
      <TableCell
        key={col.key}
        className={classes.tableCell}
        onClick={() => col.sortable && handleSort(col.key)}
        style={{ cursor: col.sortable ? "pointer" : "default" }}
      >
        <div className={classes.tableHeaderIcon}>
          <span>{getAltLabel(col.header_key, userDetails, t)}</span>
          {col.sortable && (
            <FontAwesomeIcon
              icon={
                sortConfig.key === col.key
                  ? sortConfig.direction === "ascending" ? faSortUp : faSortDown
                  : faSort
              }
              className={
                sortConfig.key === col.key
                  ? `${classes.sortIcon} ${classes.activeSortIcon}`
                  : classes.sortIcon
              }
            />
          )}
        </div>
      </TableCell>
    ))}
  </TableRow>
</TableHead>
<TableBody>
  {sortedAndFilteredInitiatives.map((initiative) => (
    <TableRow key={initiative.id}>
      {/* ENGLISH: Dynamically render table cells based on config and column type */}
      {(tableLayout.columns || []).map((col) => {
        const value = initiative[col.key];

        const renderCellContent = () => {
          switch (col.type) {
            case "status_color":
              return (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "15px",
                      height: "15px",
                      borderRadius: "50%",
                      backgroundColor:
                        value === "red" ? "#E04F5F"
                        : value === "yellow" ? "#F4C430"
                        : value === "green" ? "green"
                        : "gray",
                      margin: "-5px 8px",
                    }}
                  ></span>
                  {userDetails?.has_status_warning !== false && (
                    <StatusWarningIndicator
                      initiativeId={initiative.id}
                      currentStatus={value}
                      size="14px"
                      color="#ff9800"
                      showTooltip={true}
                      lazyLoad={false}
                    />
                  )}
                </div>
              );
            case "link_text":
              return (
                <span
                  className={classes.clickableInitiative}
                  onClick={() => handleInitiativeClick(initiative.id)}
                >
                  {value}
                </span>
              );
            case "stage":
                 return t(`initiatives.stages.${value}`);
            case "number":
              // Para campos de deliverables, não usar unidade (são contadores, não valores monetários)
              if (col.key.includes("deliverables")) {
                // console.log(`🔍 [DEBUG] Campo ${col.key}: valor=${value}, kpi_unit=${initiative.kpi_unit}`);
                return formatNumber(value || 0, "", false); // Sem unidade para deliverables
              }
              // Para outros campos numéricos (KPIs), usar a unidade da iniciativa
              return formatNumber(value || 0, initiative.kpi_unit, true);
            case "percentage":
              return value && value !== 0 && !isNaN(value)
                ? `${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`
                : "-";
            
            case "custom_field":
            case "custom_number":
            case "custom_boolean":
              // Extrair o nome do campo: "custom:baseline_value" → "baseline_value"
              const fieldName = col.key.replace("custom:", "");
              const customValue = initiative.customFields?.[fieldName] || "-";
              
              if (col.type === "custom_boolean") {
                return customValue === "true" ? "✓" : "✗";
              } else if (col.type === "custom_number") {
                return customValue !== "-" ? formatNumber(customValue, "", false) : "-";
              } else {
                return customValue;
              }
            
            case "text":
            default:
              // Para o campo leader, se já vier formatado do backend, usar direto
              if (col.key === "leader") {
                // Se o leader já contém vírgula, significa que já foi formatado no backend
                if (initiative.leader && initiative.leader.includes(',')) {
                  return initiative.leader;
                }
                // Caso contrário, formatar no frontend
                return formatLeadersNames(initiative.leader, initiative.leader2);
              }
              return value;
          }
        };

        return (
          <TableCell key={`${initiative.id}-${col.key}`} style={{ textAlign: 'left' }}>
            {renderCellContent()}
          </TableCell>
        );
      })}
    </TableRow>
  ))}
</TableBody>
                          </Table>
                        </TableContainer>
                      </div>
                    </CardBody>
                  )}
                  {initiativesView === "board" && (
                    <CardBody>
                      <DragDropContext 
                        onDragStart={onDragStartInitiatives}
                        onDragEnd={onDragEndInitiatives}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${stages.length}, 1fr)`,
                            gap: "20px",
                            padding: "3px 0",
                            overflowX: "auto",
                          }}
                        >
                          {stages.map((stageKey) => {
                            const stageItems =
                              sortedAndFilteredInitiatives.filter(
                                (initiative) => initiative.stage === stageKey
                              );

                            // Verifica se este stage pode receber drop
                            const isDropDisabled = draggingInitiative ? 
                              !isDropAllowed(draggingInitiative.stage, stageKey) : 
                              false;

                            return (
                              <Droppable 
                                droppableId={stageKey} 
                                key={stageKey}
                                isDropDisabled={isDropDisabled}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    className={classes.boardColumn}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                      opacity: isDropDisabled ? 0.4 : 1,
                                      backgroundColor: isDropDisabled ? '#f5f5f5' : 
                                                      snapshot.isDraggingOver ? '#e8f5e9' : 'white',
                                      border: isDropDisabled ? '2px dashed #ccc' : '1px solid #e0e0e0',
                                      transition: 'all 0.2s ease',
                                      position: 'relative',
                                      // Colunas SEMPRE com z-index baixo, mesmo quando isDraggingOver
                                      zIndex: 1,
                                    }}
                                  >
                                    {/* ───────────  MINI‑RESUMO DO ESTÁGIO ─────────── */}

                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        gap: 8,
                                        marginTop: 1,
                                        padding: 3,
                                        marginBottom: 8,
                                        flexWrap: "wrap",
                                        border: "1px solid #eee",
                                        borderRadius: "4px",
                                        backgroundColor: "#e4e4e4", // quebra em 2ª linha se tela estreita
                                      }}
                                    >
                                      <StageNumber
                                        label={getAltLabel("initiatives.total_initiatives", userDetails, t)}
                                        value={stageSummary[stageKey].count}
                                      />

                                      <StageNumber
                                        grow
                                        label={getAltLabel(
                                          boardCard ? boardCard.label_key : "initiatives.columns.kpi_planned",
                                          userDetails,
                                          t
                                        )}
                                        value={(() => {
                                          const value = stageSummary[stageKey].dynamic_value || 0;
                                          
                                          if (!boardCard) {
                                            // Fallback para o comportamento antigo
                                            return formatNumber(
                                              stageSummary[stageKey].kpi_planned_total,
                                              sortedAndFilteredInitiatives[0]?.kpi_unit || "",
                                              true
                                            );
                                          }

                                          // Renderizar baseado no tipo do card
                                          switch (boardCard.type) {
                                            case "count":
                                              return value.toString();
                                            case "number_sum":
                                              // Para deliverables, não usar unidade
                                              if (boardCard.key.includes("deliverables")) {
                                                return formatNumber(value, "", false);
                                              }
                                              // Para KPIs, usar a unidade da iniciativa
                                              return formatNumber(value, sortedAndFilteredInitiatives[0]?.kpi_unit || "", true);
                                            case "percentage_avg":
                                              return `${value.toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
                                            default:
                                              return value.toString();
                                          }
                                        })()}
                                      />
                                    </div>
                                    <Typography
                                      variant="body1"
                                      className={classes.boardColumnHeader}
                                    >
                                      {t(`initiatives.stages.${stageKey}`)}
                                    </Typography>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "10px",
                                      }}
                                    >
                                      {stageItems.length === 0 ? (
                                        <Typography
                                          variant="caption"
                                          style={{
                                            color: "#aaa",
                                            marginLeft: "4px",
                                          }}
                                        >
                                          {t("initiatives.no_initiatives_here")}
                                        </Typography>
                                      ) : (
                                        [...stageItems]
                                          .sort((a, b) => {
                                            const sA =
                                              statusOrder[a.status] || 99;
                                            const sB =
                                              statusOrder[b.status] || 99;
                                            return sA - sB;
                                          })
                                          .map((item, index) => {
                                            // Define a cor baseada no status, garantindo a conversão para minúsculas
                                            const statusColor = (
                                              item.status || ""
                                            ).toLowerCase();
                                            const cardBorderColor =
                                              statusColor === "green"
                                                ? "green"
                                                : statusColor === "yellow"
                                                  ? "#F4C430"
                                                  : statusColor === "red"
                                                    ? "#E04F5F"
                                                    : "transparent";

                                            // Verifica a permissão para arrastar usando a nova estrutura de permissões
                                            const canDrag =
                                              userDetails?.permissions
                                                ?.can_change_initiative_stage;

                                            if (canDrag) {
                                              return (
                                                <Draggable
                                                  key={item.id}
                                                  draggableId={String(item.id)}
                                                  index={index}
                                                >
                                                  {(dragProvided, dragSnapshot) => {
                                                    // Cria estilo customizado para garantir z-index alto quando arrastando
                                                    const customStyle = dragSnapshot.isDragging ? {
                                                      ...dragProvided.draggableProps.style,
                                                      borderTop: `4px solid ${cardBorderColor}`,
                                                      backgroundColor: '#e0e0e0',
                                                      boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                                                    } : {
                                                      ...dragProvided.draggableProps.style,
                                                      borderTop: `4px solid ${cardBorderColor}`,
                                                    };

                                                    const cardContent = (
                                                    <div
                                                      className={
                                                        classes.cardItem
                                                      }
                                                      ref={
                                                        dragProvided.innerRef
                                                      }
                                                      {...dragProvided.draggableProps}
                                                      {...dragProvided.dragHandleProps}
                                                      style={customStyle}
                                                      onMouseEnter={(e) => {
                                                        if (!dragSnapshot.isDragging) {
                                                          e.currentTarget.style.transform =
                                                            "scale(1.01)";
                                                        }
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        if (!dragSnapshot.isDragging) {
                                                          e.currentTarget.style.transform =
                                                            "scale(1)";
                                                        }
                                                      }}
                                                    >
                                                      {/* O conteúdo interno do card permanece o mesmo */}
                                                      {loadingInitiativeId ===
                                                      item.id ? (
                                                        <div
                                                          style={{
                                                            display: "flex",
                                                            justifyContent:
                                                              "center",
                                                            alignItems:
                                                              "center",
                                                            height: 80,
                                                          }}
                                                        >
                                                          <ThreeDots
                                                            color={
                                                              userDetails.implementation_color
                                                            }
                                                            height={40}
                                                            width={40}
                                                          />
                                                        </div>
                                                      ) : (
                                                        <>
                                                          <div
                                                            style={{
                                                              display: "flex",
                                                              alignItems:
                                                                "center",
                                                              gap: 6,
                                                              cursor: "pointer",
                                                            }}
                                                            onClick={() =>
                                                              handleInitiativeClick(
                                                                item.id
                                                              )
                                                            }
                                                          >
                                                            {item.approval_status ===
                                                              "rejected" && (
                                                              <Cancel
                                                                style={{
                                                                  color:
                                                                    "#E04F5F",
                                                                  fontSize: 18,
                                                                }}
                                                              />
                                                            )}
                                                            {item.approval_status ===
                                                              "pending" && (
                                                              <HourglassEmpty
                                                                style={{
                                                                  color: "#999",
                                                                  fontSize: 18,
                                                                }}
                                                              />
                                                            )}
                                                            <Tooltip
                                                              title={
                                                                item.initiative
                                                              }
                                                              arrow
                                                            >
                                                              <Typography
                                                                variant="body2"
                                                                className={
                                                                  classes.ellipsisText
                                                                }
                                                                style={{
                                                                  fontWeight: 600,
                                                                }}
                                                              >
                                                                {
                                                                  item.initiative
                                                                }
                                                              </Typography>
                                                            </Tooltip>
                                                          </div>
                                                          <Typography
                                                            variant="caption"
                                                            style={{
                                                              display: "block",
                                                            }}
                                                          >
                                                            {t(
                                                              "initiatives.columns.leader"
                                                            )}
                                                            : {item.leader && item.leader.includes(',') ? item.leader : formatLeadersNames(item.leader, item.leader2)}
                                                          </Typography>
                                                          <Typography
                                                            variant="caption"
                                                            style={{
                                                              display: "block",
                                                            }}
                                                          >
                                                            {getAltLabel(
                                                              "initiatives.columns.workfront",
                                                              userDetails,
                                                              t
                                                            )}
                                                            : {item.workfront}
                                                          </Typography>
                                                        </>
                                                      )}
                                                    </div>
                                                    );

                                                    // Se está arrastando, renderiza via portal para garantir z-index
                                                    if (dragSnapshot.isDragging) {
                                                      return ReactDOM.createPortal(cardContent, portal);
                                                    }

                                                    return cardContent;
                                                  }}
                                                </Draggable>
                                              );
                                            } else {
                                              // Renderiza o card não-arrastável
                                              return (
                                                <div
                                                  key={item.id}
                                                  className={classes.cardItem}
                                                  onClick={() =>
                                                    handleInitiativeClick(
                                                      item.id
                                                    )
                                                  }
                                                  style={{
                                                    borderTop: `4px solid ${cardBorderColor}`,
                                                    cursor: "pointer", // Alterado para pointer para indicar que é clicável
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform =
                                                      "scale(1.01)";
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform =
                                                      "scale(1)";
                                                  }}
                                                >
                                                  {/* O conteúdo interno do card é o mesmo do Draggable */}
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 6,
                                                    }}
                                                  >
                                                    {item.approval_status ===
                                                      "rejected" && (
                                                      <Cancel
                                                        style={{
                                                          color: "#E04F5F",
                                                          fontSize: 18,
                                                        }}
                                                      />
                                                    )}
                                                    {item.approval_status ===
                                                      "pending" && (
                                                      <HourglassEmpty
                                                        style={{
                                                          color: "#999",
                                                          fontSize: 18,
                                                        }}
                                                      />
                                                    )}
                                                    <Tooltip
                                                      title={item.initiative}
                                                      arrow
                                                    >
                                                      <Typography
                                                        variant="body2"
                                                        className={
                                                          classes.ellipsisText
                                                        }
                                                        style={{
                                                          fontWeight: 600,
                                                        }}
                                                      >
                                                        {item.initiative}
                                                      </Typography>
                                                    </Tooltip>
                                                  </div>
                                                  <Typography
                                                    variant="caption"
                                                    style={{ display: "block" }}
                                                  >
                                                    {t(
                                                      "initiatives.columns.leader"
                                                    )}
                                                    : {item.leader && item.leader.includes(',') ? item.leader : formatLeadersNames(item.leader, item.leader2)}
                                                  </Typography>
                                                  <Typography
                                                    variant="caption"
                                                    style={{ display: "block" }}
                                                  >
                                                    {getAltLabel(
                                                      "initiatives.columns.workfront",
                                                      userDetails,
                                                      t
                                                    )}
                                                    : {item.workfront}
                                                  </Typography>
                                                </div>
                                              );
                                            }
                                          })
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  </div>
                                )}
                              </Droppable>
                            );
                          })}
                        </div>
                      </DragDropContext>
                    </CardBody>
                  )}
                  {initiativesView === "gantt" && (
                    <CardBody>
                      <ConsolidatedInitiativesGantt
                        initiatives={sortedAndFilteredInitiatives}
                        classes={classes}
                        t={t}
                        onSelectInitiative={handleInitiativeClick}
                        userDetails={userDetails}
                      />
                    </CardBody>
                  )}
                </div>
              </>
            )}
          </Card>
        </GridItem>
        {/* ONLY shows this block if a initiative is selected */}
        {selectedInitiative && (
          <GridItem xs={12}>
            {isLoadingDetails ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <ThreeDots
                  color={userDetails.implementation_color}
                  height={80}
                  width={80}
                  ariaLabel="loading"
                />
              </div>
            ) : (
              <Card style={{ marginTop: "10px" }}>
                <h4
                  className={classes.initiativeDetailsHeader}
                  style={{
                    borderBottom: `1px solid ${addTransparency(clientColor, 0.2)}`,
                  }}
                >
                  {selectedSection === "details"
                    ? getAltLabel("initiatives.details", userDetails, t)
                    : selectedSection === "approvals"
                      ? t("initiative_selected.approvals")
                      : selectedSection === "one_page"
                        ? t("initiative_selected.one_page")
                        : selectedSection === "execution"
                          ? t("initiative_selected.execution")
                          : selectedSection === "results"
                            ? t("initiative_selected.results")
                            : t("initiative_selected.edit")}
                </h4>
                <CardBody style={{ padding: "10px" }}>
                  <div>
                    {selectedSection === "details" && selectedInitiative && (
                      <InitiativeDetails
                        t={t}
                        classes={classes}
                        clientColor={clientColor}
                        selectedInitiative={selectedInitiative}
                        userDetails={userDetails}
                        customStyles={customStyles}
                        renderCustomFieldsDetail={renderCustomFieldsDetail}
                        renderKpis={renderKpis}
                        i18n={i18n}
                        /* toggle sections as needed */
                        showIdentification
                        showDates
                        showDescription
                        showCustomFields
                        showKpis
                        showClassification
                        showEditors={userDetails.has_forms_editors}
                        showAttachments
                        onDownloadFile={secureDownload}
                      />
                    )}
                    {userDetails.has_approvals_stages &&
                      selectedSection === "approvals" &&
                      selectedInitiative &&
                      selectedInitiative.id && (
                        <ApprovalsSection
                          selectedInitiative={selectedInitiative}
                          t={t}
                          classes={classes}
                          clientColor={clientColor}
                        />
                      )}
                    {userDetails.has_op_info &&
                      selectedSection === "one_page" &&
                      selectedInitiative &&
                      selectedInitiative.id && (
                        (() => {
                          // console.log("Rendering OnePage with selectedInitiative:", selectedInitiative);
                          return (
                            <OnePage
                              selectedInitiative={selectedInitiative}
                              onePageInfoData={onePageInfoData}
                              setOnePageInfoData={setOnePageInfoData}
                              fetchOnePageData={fetchOnePageData}
                              onePageHasApproval={userDetails.has_op_approval}
                              t={t}
                              classes={classes}
                              addTransparency={addTransparency}
                              canReport={
                                userDetails?.permissions?.can_report_one_page
                              }
                              canApprove={
                                userDetails?.permissions?.can_approve_one_page &&
                                (userDetails.email ===
                                  selectedInitiative.leader_email ||
                                  userDetails.email ===
                                    selectedInitiative.pmo_email)
                              }
                              clientColor={clientColor}
                              showContent={true} // opcional, true por padrão
                            />
                          );
                        })()
                      )}
                    {userDetails.has_deliverables_info &&
                      selectedSection === "execution" &&
                      selectedInitiative &&
                      selectedInitiative.id && (
                        <InitiativesImplementation
                          selectedInitiative={selectedInitiative}
                          deliverablesData={dataDeliverables}
                          canEdit={
                            userDetails?.permissions?.can_edit_deliverables
                          }
                          canApprove={
                            userDetails?.permissions?.can_approve_deliverables
                          }
                          classes={classes}
                          loading={loadingDeliverables}
                          clientColor={clientColor}
                        />
                      )}
                    {userDetails.has_results_info &&
                      selectedSection === "results" &&
                      selectedInitiative &&
                      selectedInitiative.id && (
                        <div>
                          <InitiativeResultsPage
                            selectedInitiative={selectedInitiative}
                            t={t}
                            classes={classes}
                            clientColor={clientColor}
                            initiativeKpisData={initiativeKpisData}
                            allowKpiEdits={
                              userDetails?.permissions?.can_report_kpi_values
                            }
                            canEdit={
                              userDetails?.permissions?.can_report_kpi_values
                            }
                            // ADICIONADO: Lógica de permissão de aprovação contextual
                            canApprove={
                              userDetails?.permissions
                                ?.can_approve_kpi_values &&
                              (userDetails.email ===
                                selectedInitiative.leader_email ||
                                userDetails.email ===
                                  selectedInitiative.pmo_email)
                            }
                            onReloadKpis={fetchKpiData} // se você já passa onReloadKpis
                            onReloadInitiatives={fetchInitiatives} // <-- LINHA NOVA
                          />
                        </div>
                      )}
                  </div>
                </CardBody>
              </Card>
            )}
          </GridItem>
        )}
      </GridContainer>
    </>
  );
}
